import * as React from "react"

import { cn } from "@/lib/utils"

export interface InputProps extends React.ComponentProps<"input"> {
  label?: string
  error?: string
  description?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, description, id, "aria-describedby": ariaDescribedBy, lang, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const descriptionId = description ? `${inputId}-description` : undefined
    const errorId = error ? `${inputId}-error` : undefined
    const describedBy = [ariaDescribedBy, errorId || descriptionId].filter(Boolean).join(" ") || undefined
    const placeholderFallback = !label && !id ? props.placeholder : undefined
    const accessibleName = props["aria-label"] || placeholderFallback

    const control = (
      <input
        id={inputId}
        type={type}
          lang={type && ["date", "time", "datetime-local", "month", "week", "number"].includes(type) ? "en" : lang}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          error && "border-destructive focus-visible:ring-destructive",
          className
        )}
        ref={ref}
        aria-label={accessibleName}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...props}
      />
    )

    if (!label) return control

    return (
      <div className="flex w-full flex-col gap-1.5">
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">{label}</label>
        {control}
        {description && !error ? <p id={descriptionId} className="text-xs text-muted-foreground">{description}</p> : null}
        {error ? <p id={errorId} role="alert" className="text-xs text-destructive">{error}</p> : null}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
